import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import useAuthStore from './stores/authStore';
import authApi from './services/authApi';

// Pages
import Login from './pages/auth/Login';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import ScenarioSelection from './pages/ScenarioSelection';
import TrainingPage from './pages/TrainingPage';
import TrainingMode from './pages/TrainingMode';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import ScenarioManagement from './pages/admin/ScenarioManagement';
import PromptEditor from './pages/admin/PromptEditor';
import Analytics from './pages/admin/Analytics';
import ServiceAnalytics from './pages/admin/ServiceAnalytics';
import OrganizationManagement from './pages/admin/OrganizationManagement';
import AuditLog from './pages/admin/AuditLog';
import OrganizationHierarchy from './pages/admin/OrganizationHierarchy';

// User Pages
import UserLayout from './components/user/UserLayout';
import UserDashboard from './pages/user/UserDashboard';
import SessionHistory from './pages/user/SessionHistory';
import Profile from './pages/user/Profile';
import UserStats from './pages/user/UserStats';
import TeamDashboard from './pages/user/TeamDashboard';
import LeaderDashboard from './pages/director/LeaderDashboard';
import OrganizationWorkspace from './pages/director/OrganizationWorkspace';
import KnowledgeBase from './components/KnowledgeBase';

// Protected Routes
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import ScenarioAccessGuard from './components/training/ScenarioAccessGuard';

function App() {
  const { isAuthenticated, user, login, logout } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(true);

  // Auto-restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // If user data exists in localStorage
        // Try to refresh the session using refresh token from cookie
        if (user) {
          console.log('Found user in localStorage, attempting to restore session...');
          const response = await authApi.refreshAccessToken();

          if (response.success && response.data.accessToken) {
            // Restore session with new access token
            login(user, response.data.accessToken);
            console.log('✓ Session restored successfully');
          }
        } else {
          console.log('No user in localStorage, skipping session restore');
        }
      } catch (error) {
        console.error('Failed to restore session:', error);
        // If refresh fails, clear invalid session data
        if (user) {
          console.log('Clearing invalid session data');
          logout();
        }
      } finally {
        setIsInitializing(false);
      }
    };

    restoreSession();
  }, []); // Run only once on mount

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" />

      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          }
        />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />

        {/* User routes - Authenticated users with UserLayout */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <UserLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<UserDashboard />} />
          <Route path="training-mode" element={<TrainingMode />} />
          <Route
            path="scenarios"
            element={<ScenarioSelection />}
          />
          <Route path="history" element={<SessionHistory />} />
          <Route path="stats" element={<UserStats />} />
          <Route path="knowledge" element={<KnowledgeBase />} />
          <Route path="profile" element={<Profile />} />
          <Route path="team" element={<TeamDashboard />} />
          <Route path="leader" element={<LeaderDashboard />} />
          <Route path="organization" element={<OrganizationWorkspace />} />
        </Route>

        {/* Training page - separate from UserLayout for full screen */}
        <Route
          path="/training/:scenarioId"
          element={
            <PrivateRoute>
              <ScenarioAccessGuard>
                <TrainingPage />
              </ScenarioAccessGuard>
            </PrivateRoute>
          }
        />

        {/* Admin routes - Admin only */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="organizations" element={<OrganizationManagement />} />
          <Route path="hierarchy" element={<OrganizationHierarchy />} />
          <Route path="scenarios" element={<ScenarioManagement />} />
          <Route path="audit-log" element={<AuditLog />} />
          <Route path="scenarios/:scenarioId/prompt" element={<PromptEditor />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="services" element={<ServiceAnalytics />} />
        </Route>

        {/* Catch all - redirect to dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
